import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '~/modules/components/global/button';
import { Button as ShadcnButton } from '~/components/ui/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import { Input } from '~/components/ui/input';
import { Badge } from '~/components/ui/badge';
import {
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '~/components/ui/select';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '~/components/ui/table';
import { Label } from '~/components/ui/label';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import PersonalAccessTokenService from '~/modules/services/personal-access-token-service';
import ApiTokenRevealModal from './api-token-reveal-modal';
import { captureError } from '../../../pixwel/exception-handler.factory';

const QUERY_KEY = ['personalAccessTokens'];

const EXPIRY_OPTIONS = [
	{ label: '30 days', value: 30 },
	{ label: '60 days', value: 60 },
	{ label: '90 days', value: 90 },
	{ label: '180 days', value: 180 },
	{ label: '1 year', value: 365 }
];

const formatDate = (timestamp) => {
	if(!timestamp) {
		return null;
	}
	return new Date(timestamp * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatRelative = (timestamp) => {
	if(!timestamp) {
		return 'Never';
	}
	const diff = Date.now() - timestamp * 1000;
	const minutes = Math.floor(diff / 60000);
	if(minutes < 1) {
		return 'Just now';
	}
	if(minutes < 60) {
		return `${minutes}m ago`;
	}
	const hours = Math.floor(minutes / 60);
	if(hours < 24) {
		return `${hours}h ago`;
	}
	const days = Math.floor(hours / 24);
	if(days < 30) {
		return `${days}d ago`;
	}
	return formatDate(timestamp);
};

const ExpiryCell = ({ timestamp }) => {
	if(!timestamp) {
		return <span className="text-muted-foreground">—</span>;
	}
	const daysLeft = Math.floor((timestamp * 1000 - Date.now()) / 86400000);
	if(daysLeft < 0) {
		return <Badge className="bg-destructive/20 text-destructive border-destructive/30">Expired</Badge>;
	}
	if(daysLeft <= 14) {
		return (
			<span className="flex items-center gap-2">
				<Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Expires soon</Badge>
				<span className="text-xs text-muted-foreground">{formatDate(timestamp)}</span>
			</span>
		);
	}
	return <span className="text-sm">{formatDate(timestamp)}</span>;
};

ExpiryCell.propTypes = {
	timestamp: PropTypes.number
};

const expiresAtFromDays = (days) => Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;

const ApiTokens = () => {
	const queryClient = useQueryClient();

	const [name, setName] = useState('');
	const [expiryDays, setExpiryDays] = useState(90);
	const [revealedToken, setRevealedToken] = useState(null);

	const { data: tokens = [], isLoading } = useQuery(
		QUERY_KEY,
		() => PersonalAccessTokenService.list(),
		{ refetchOnWindowFocus: false }
	);

	const { mutateAsync: createToken, isLoading: isCreating } = useMutation(
		({ name, expiresAt }) => PersonalAccessTokenService.create({ name, expiresAt }),
		{
			onSuccess: (token) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setRevealedToken(token);
				setName('');
				setExpiryDays(90);
			},
			onError: (err) => {
				const message = err?.response?.data?.error || err.message || 'Failed to create token';
				ToastService.create('error', message);
				captureError(err, `Error creating personal access token: ${err.message}`);
			}
		}
	);

	const { mutateAsync: revokeToken } = useMutation(
		(id) => PersonalAccessTokenService.revoke(id),
		{
			onSuccess: (_, id) => {
				queryClient.setQueryData(QUERY_KEY, (old = []) => old.filter((t) => t._id !== id));
				queryClient.invalidateQueries(QUERY_KEY);
				ToastService.create('success', 'Token revoked');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to revoke token');
				captureError(err, `Error revoking personal access token: ${err.message}`);
			}
		}
	);

	const handleCreate = async() => {
		const trimmed = name.trim();
		if(!trimmed) {
			return;
		}
		await createToken({ name: trimmed, expiresAt: expiresAtFromDays(expiryDays) });
	};

	const handleRevoke = async(row) => {
		const ok = await confirm({
			title: 'Revoke API Token',
			content: `Revoke "${row.name}"? Anything using this token will stop working.`,
			confirmText: 'Revoke',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		await revokeToken(row._id);
	};

	return (
		<div data-testid="preferences-api-tokens">
			<div className="mb-8">
				<h3 className="text-base font-semibold mb-1">Create API token</h3>
				<p className="text-sm text-muted-foreground mb-4">
					Tokens act on your behalf with your full account permissions. Treat them like a password —
					the value is only shown once.
				</p>
				<div className="flex gap-3 items-end flex-wrap">
					<div className="flex flex-col gap-1.5 flex-1 min-w-[14rem]">
						<Label htmlFor="api-token-name">Name</Label>
						<Input
							id="api-token-name"
							placeholder="e.g. ci-deploy"
							value={name}
							onChange={(e) => setName(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
							data-testid="api-token-name-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5 w-36">
						<Label>Expires in</Label>
						<Select value={String(expiryDays)} onValueChange={(val) => setExpiryDays(Number(val))}>
							<SelectTrigger data-testid="api-token-expiry-select">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{EXPIRY_OPTIONS.map((opt) => (
									<SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button
						onClick={handleCreate}
						loading={isCreating}
						disabled={!name.trim() || isCreating}
						data-testid="api-token-create-button"
					>
						Create token
					</Button>
				</div>
			</div>

			<div>
				<h3 className="text-base font-semibold mb-3">Active tokens</h3>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Token</TableHead>
							<TableHead>Last used</TableHead>
							<TableHead>Expires</TableHead>
							<TableHead></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading
							? (
								<TableRow>
									<TableCell colSpan={5} className="text-center text-muted-foreground">Loading…</TableCell>
								</TableRow>
							)
							: tokens.length === 0
								? (
									<TableRow>
										<TableCell colSpan={5}>
											<EmptyState message="No API tokens" />
										</TableCell>
									</TableRow>
								)
								: (
									tokens.map((token) => (
										<TableRow key={token._id}>
											<TableCell className="font-medium max-w-[10rem] truncate">{token.name}</TableCell>
											<TableCell>
												<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
											pat_{token.tokenPrefix}…
												</code>
											</TableCell>
											<TableCell className="text-muted-foreground text-sm">{formatRelative(token.lastUsedAt)}</TableCell>
											<TableCell><ExpiryCell timestamp={token.expiresAt} /></TableCell>
											<TableCell className="text-right">
												<ShadcnButton
													variant="ghost"
													size="icon"
													aria-label="Revoke token"
													title="Revoke token"
													data-testid="api-token-revoke-button"
													onClick={() => handleRevoke(token)}
												>
													<Icon use="trash" color="red-base" size={0.875} />
												</ShadcnButton>
											</TableCell>
										</TableRow>
									))
								)}
					</TableBody>
				</Table>
			</div>

			<ApiTokenRevealModal
				token={revealedToken}
				onClose={() => setRevealedToken(null)}
			/>
		</div>
	);
};

export default ApiTokens;
